// 施工箇所タブ: フィルタパネル + 場所区分階層ビュー(mock 準拠)。
// 末端は施工箇所 1 件 = 1 行のアコーディオン表で、工程進捗 Chip と
// 「全工程を開始 / 施工を再開 / この工程を開始」の開始動線を持つ。

import { useMemo, useCallback, useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Checkbox, Collapse, FormControlLabel, IconButton, MenuItem,
  Paper, RadioGroup, Radio, Stack, Table, TableHead, TableBody, TableRow, TableCell, TextField, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { HierarchicalView, HierarchyBuilder, LeafRowInfo } from "../../../components/HierarchicalView";
import { ExtendedDB } from "../../../types/extendedDB";
import { ConstructionPlanAreaBase } from "../../../schema/generated/ConstructionPlanArea";
import { getAreaSlots, slotsToText } from "../../../util/areaStatus";
import Time from "../../../util/Time";
import { AreaDraftStatus, getAreaDraftStatus, getAreaDraftConstructionId } from "./helpers";
import { EmptyState } from "../../../components/EmptyState";

type DateMode = "today" | "range" | "all";

// 階層ビュー末端(施工箇所)ノードに載せるデータ。
type ExecuteLeafData = { areaId: string; area: ConstructionPlanAreaBase };

// (施工箇所×工程) が完了済みか。完了マークされた工程実施が1件でもあれば完了。
function isProcessCompleted(db: ExtendedDB, areaId: string, workDetailId: number): boolean {
  return db.processExecutions.some(
    (p) => p.deleted_at == null && p.construction_plan_area_id === areaId && p.work_detail_id === workDetailId && p.completed_at != null
  );
}

// (施工箇所×工程) を含む下書き施工実施 id。工程行の「施工を再開」の遷移先。
function getProcessDraftConstructionId(areaId: string, workDetailId: number, db: ExtendedDB): string | null {
  const pes = db.processExecutions.filter(
    (p) => p.deleted_at == null && p.construction_plan_area_id === areaId && p.work_detail_id === workDetailId
  );
  for (const pe of pes) {
    const con = db.constructions.find((c) => c.id === pe.construction_id && c.finished_at == null && c.deleted_at == null);
    if (con != null) {
      return con.id;
    }
  }
  return null;
}

export default function ExecuteListPage({ db, workFilter }: { db: ExtendedDB; workFilter: number | "" }) {
  const navigate = useNavigate();

  // --- フィルタ状態 (施工日フィルタの既定は mock 同様「本日」) ---
  const [statusFilter, setStatusFilter] = useState<Set<AreaDraftStatus>>(() => new Set<AreaDraftStatus>(["pending", "in_progress"]));
  const [dateMode, setDateMode] = useState<DateMode>("today");
  const todayStr = Time.TodayString();
  const [rangeStart, setRangeStart] = useState<string>(todayStr);
  const [rangeEnd, setRangeEnd] = useState<string>(todayStr);
  const [workTypeFilter, setWorkTypeFilter] = useState<number | "">("");
  const [workSubtypeFilter, setWorkSubtypeFilter] = useState<number | "">("");
  const [workDetailFilter, setWorkDetailFilter] = useState<number | "">("");
  // アコーディオン展開状態(施工箇所単位)。
  const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(new Set());

  // 工種が変わったら種別・工程をリセット
  useEffect(() => { setWorkSubtypeFilter(""); setWorkDetailFilter(""); }, [workTypeFilter]);
  // 種別が変わったら工程をリセット
  useEffect(() => { setWorkDetailFilter(""); }, [workSubtypeFilter]);

  const toggleStatus = useCallback((s: AreaDraftStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { next.delete(s); } else { next.add(s); }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((areaId: string) => {
    setExpandedAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) { next.delete(areaId); } else { next.add(areaId); }
      return next;
    });
  }, []);

  // 施工日フィルタの範囲 (planned_start/end は ms タイムスタンプ)。
  // 開始 = DateInputToEpoch(start)、終了 = DateInputToEpoch(end) + Time.ADay(終日を含める上限)。
  const filterRange = useMemo<{ startMs: number; endMs: number } | null>(() => {
    if (dateMode === "all") return null;
    const startStr = dateMode === "today" ? todayStr : (rangeStart || todayStr);
    const endStr = dateMode === "today" ? todayStr : (rangeEnd || rangeStart || todayStr);
    const startMs = Time.DateInputToEpoch(startStr) ?? Time.DateInputToEpoch(todayStr);
    const endMs = Time.DateInputToEpoch(endStr) ?? Time.DateInputToEpoch(todayStr);
    if (startMs == null || endMs == null) {
      return null;
    }
    return { startMs, endMs: endMs + Time.ADay };
  }, [dateMode, todayStr, rangeStart, rangeEnd]);

  // フィルタ済み施工箇所
  const filteredAreas = useMemo(() => {
    return db.constructionPlanAreas.filter((a) => {
      if (a.deleted_at != null) return false;
      if (workFilter !== "" && a.work_id !== workFilter) return false;
      if (workTypeFilter !== "" && a.work_type_id !== workTypeFilter) return false;
      if (workSubtypeFilter !== "" && a.work_subtype_id !== workSubtypeFilter) return false;

      // 施工日フィルタ (planned_start/end はタイムスタンプ ms)
      if (filterRange != null) {
        const ps = a.planned_start;
        const pe = a.planned_end;
        if (ps != null && pe != null) {
          if (pe < filterRange.startMs || ps >= filterRange.endMs) return false;
        } else if (ps != null) {
          if (ps >= filterRange.endMs) return false;
        } else if (pe != null) {
          if (pe < filterRange.startMs) return false;
        }
      }

      // 工程フィルタ ON 時: その工程が存在する施工箇所のみ + 施工状況フィルタをその工程に適用
      if (workDetailFilter !== "") {
        const wds = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === a.work_subtype_id);
        const targetWd = wds.find((d) => d.id === workDetailFilter);
        if (!targetWd) return false;
        const pesForTarget = db.processExecutions.filter(
          (p) => p.construction_plan_area_id === a.id && p.work_detail_id === targetWd.id && p.deleted_at == null
        );
        if (pesForTarget.length === 0) return statusFilter.has("pending");
        const conIdsT = new Set(pesForTarget.map((p) => p.construction_id));
        const hasDraftT = db.constructions.some((c) => conIdsT.has(c.id) && c.finished_at == null && c.deleted_at == null);
        return statusFilter.has(hasDraftT ? "in_progress" : "done");
      }

      return statusFilter.has(getAreaDraftStatus(a, db));
    });
  }, [
    db.constructionPlanAreas, db.processExecutions, db.constructions, db.workDetails,
    workFilter, workTypeFilter, workSubtypeFilter, workDetailFilter,
    statusFilter, filterRange,
  ]);

  // 施工方法フィルタの選択肢 (工事フィルタ済みの全施工箇所ベース)
  const workTypeOptions = useMemo(() => {
    const set = new Set<number>();
    for (const a of db.constructionPlanAreas) {
      if (a.deleted_at != null) continue;
      if (workFilter !== "" && a.work_id !== workFilter) continue;
      if (a.work_type_id != null) set.add(a.work_type_id);
    }
    return Array.from(set)
      .map((id) => db.workTypes.find((w) => w.id === id))
      .filter((w): w is NonNullable<typeof w> => w != null && w.deleted_at == null);
  }, [db.constructionPlanAreas, db.workTypes, workFilter]);

  const workSubtypeOptions = useMemo(() => {
    if (workTypeFilter === "") return [];
    return db.workSubtypes.filter((s) => s.deleted_at == null && s.work_type_id === workTypeFilter);
  }, [db.workSubtypes, workTypeFilter]);

  const workDetailOptions = useMemo(() => {
    if (workSubtypeFilter === "") return [];
    return db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === workSubtypeFilter);
  }, [db.workDetails, workSubtypeFilter]);

  // 場所区分スロットのラベル名(工事 → 区分エリア → スロット別ラベル)。未設定は「区分N」。
  const slotLabelOf = useCallback((sampleArea: ConstructionPlanAreaBase | undefined, slot: number): string => {
    if (sampleArea?.work_id != null) {
      const work = db.works.find((w) => w.id === sampleArea.work_id);
      if (work?.area_division_id != null) {
        const label = db.areaDivisionLocationLabels.find(
          (l) => l.deleted_at == null && l.area_division_id === work.area_division_id && l.slot === slot && l.label_name !== ""
        );
        if (label != null) {
          return label.label_name;
        }
      }
    }
    return `区分${slot}`;
  }, [db.works, db.areaDivisionLocationLabels]);

  // 場所区分階層ビルダ。末端は施工箇所 (1 area = 1 leaf) で、data に area 本体を埋め込む。
  const locationAxisBuilder: HierarchyBuilder<ExecuteLeafData> = useCallback((path: string[]) => {
    const slotDivOf = (areaId: string, slot: number): number | null => {
      const hit = getAreaSlots(areaId, db.constructionPlanAreaLocations).find((s) => s.slot === slot);
      return hit?.location_division_id ?? null;
    };
    const filtered = filteredAreas.filter((a) => {
      for (let i = 0; i < path.length; i++) {
        const slot = i + 1;
        const expected = path[i] === "none" ? null : Number(path[i]);
        if (slotDivOf(a.id, slot) !== expected) {
          return false;
        }
      }
      return true;
    });
    const nextSlot = path.length + 1;
    const groups = new Map<string, ConstructionPlanAreaBase[]>();
    for (const a of filtered) {
      const v = slotDivOf(a.id, nextSlot);
      const k = v == null ? "none" : String(v);
      const arr = groups.get(k) ?? [];
      arr.push(a);
      groups.set(k, arr);
    }
    // 配下に意味のある分岐がなくなったら leaf 群を返す。
    const allLeavesNow =
      nextSlot > 10
      || (groups.size === 1 && groups.has("none"))
      || filtered.every((ar) => {
        for (let s = nextSlot; s <= 10; s++) {
          if (slotDivOf(ar.id, s) != null) {
            return false;
          }
        }
        return true;
      });

    if (allLeavesNow) {
      return {
        title: "施工箇所",
        rows: filtered.map((ar) => ({
          key: ar.id,
          label: slotsToText(ar.id, db.constructionPlanAreaLocations, db.locationDivisions) || "(場所未設定)",
          navigable: false,
          data: { areaId: ar.id, area: ar },
        })),
      };
    }

    return {
      title: slotLabelOf(filtered[0], nextSlot),
      rows: Array.from(groups.entries()).sort((x, y) => x[0].localeCompare(y[0])).map(([k2, list]) => {
        let label = "(未設定)";
        if (k2 !== "none") {
          label = db.locationDivisions.find((d) => d.id === Number(k2))?.division_name ?? k2;
        }
        return { key: k2, label: label, count: list.length };
      }),
    };
  }, [filteredAreas, db, slotLabelOf]);

  const startConstruction = useCallback((areaId: string, mode?: number | "all") => {
    const params = new URLSearchParams({ area_id: areaId });
    if (mode === "all") {
      params.set("processes", "all");
    } else if (typeof mode === "number") {
      params.set("work_detail_id", String(mode));
    }
    navigate(`new/?${params.toString()}`);
  }, [navigate]);

  const handleLeaf = useCallback((_path: string[], data: ExecuteLeafData | undefined) => {
    const areaId = data?.areaId;
    if (!areaId) return;
    navigate(`new/?area_id=${encodeURIComponent(areaId)}`);
  }, [navigate]);

  // 末端テーブル: 施工箇所 1 件 = 1 アコーディオン行(展開で工程行)。
  const renderLeavesTable = (rows: LeafRowInfo<ExecuteLeafData>[], _onLeaf: (path: string[], data: ExecuteLeafData | undefined) => void) => {
    return (
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", width: 40, p: 0.5 }} />
              <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 110 }}>工種</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 120 }}>種別</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 130 }}>工程進捗</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 220 }} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const area = r.node.data?.area;
              if (!area) return null;
              const areaRow = area;
              const wt = db.workTypes.find((w) => w.id === areaRow.work_type_id);
              const wst = db.workSubtypes.find((w) => w.id === areaRow.work_subtype_id);
              const processes = db.workDetails
                .filter((d) => d.deleted_at == null && d.work_subtype_id === areaRow.work_subtype_id)
                .sort((x, y) => x.id - y.id);
              const completedCount = processes.filter((wd) => isProcessCompleted(db, areaRow.id, wd.id)).length;
              const areaStatus = getAreaDraftStatus(areaRow, db);
              let areaDraftConId: string | null = null;
              if (areaStatus === "in_progress") {
                areaDraftConId = getAreaDraftConstructionId(areaRow, db);
              }
              const isFiltered = workDetailFilter !== "";
              const expanded = expandedAreaIds.has(areaRow.id);

              // 工程進捗 Chip: 「完了 N/M」/「未完了 N/M」。工程フィルタ ON 時は該当工程の完了/未完了のみ。
              let progressChip: React.ReactNode;
              if (isFiltered) {
                const target = processes.find((wd) => wd.id === workDetailFilter);
                if (!target) {
                  progressChip = <Typography variant="caption" color="text.secondary">-</Typography>;
                } else {
                  const done = isProcessCompleted(db, areaRow.id, target.id);
                  if (done) {
                    progressChip = <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" sx={{ pointerEvents: "none" }} />;
                  } else {
                    progressChip = <Chip size="small" color="warning" variant="outlined" label="未完了" sx={{ pointerEvents: "none" }} />;
                  }
                }
              } else if (processes.length === 0) {
                progressChip = <Typography variant="caption" color="text.secondary">工程未登録</Typography>;
              } else {
                const allDone = completedCount === processes.length;
                const partiallyDone = completedCount > 0 && !allDone;
                if (allDone) {
                  progressChip = <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`完了 ${completedCount}/${processes.length}`} sx={{ pointerEvents: "none" }} />;
                } else if (partiallyDone) {
                  progressChip = <Chip size="small" color="info" variant="outlined" label={`未完了 ${completedCount}/${processes.length}`} sx={{ pointerEvents: "none" }} />;
                } else {
                  progressChip = <Chip size="small" color="warning" variant="outlined" label={`未完了 ${completedCount}/${processes.length}`} sx={{ pointerEvents: "none" }} />;
                }
              }

              let visibleProcesses = processes;
              if (isFiltered) {
                visibleProcesses = processes.filter((p) => p.id === workDetailFilter);
              }
              const locParts = slotsToText(areaRow.id, db.constructionPlanAreaLocations, db.locationDivisions).split(" / ").filter((p) => p !== "");

              return (
                <Fragment key={r.node.key}>
                  <TableRow hover
                    sx={{
                      cursor: "pointer",
                      borderLeft: 4,
                      borderLeftColor: "success.main",
                      bgcolor: expanded ? "rgba(46,125,50,0.08)" : "rgba(46,125,50,0.04)",
                      "&:hover": { bgcolor: "rgba(46,125,50,0.10)" },
                      "& > td": { borderBottom: expanded ? "none" : undefined },
                    }}
                    onClick={() => toggleExpand(areaRow.id)}>
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small">
                        {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {locParts.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">(場所未設定)</Typography>
                      ) : (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                          {locParts.map((b, j) => (
                            <span key={j} style={{ display: "inline-flex", alignItems: "center" }}>
                              <Typography variant="body2" color="text.primary">{b}</Typography>
                              {j < locParts.length - 1 && <NavigateNextIcon fontSize="small" sx={{ color: "text.disabled", mx: 0.25 }} />}
                            </span>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2">{wt?.name ?? "-"}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{wst?.name ?? "-"}</Typography></TableCell>
                    <TableCell>{progressChip}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={0.5} justifyContent="flex-end" alignItems="center">
                        <IconButton size="small" color="primary"
                          title="施工箇所ページを開く"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/console/construction-plan/area/${areaRow.id}/`);
                          }}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        {areaStatus === "done" && (
                          <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" sx={{ pointerEvents: "none" }} />
                        )}
                        {areaStatus !== "done" && areaDraftConId != null && (
                          <Button size="small" variant="contained" color="warning"
                            startIcon={<PlayArrowIcon fontSize="small" />}
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`${areaDraftConId}/edit/`);
                            }}>
                            施工を再開
                          </Button>
                        )}
                        {areaStatus !== "done" && areaDraftConId == null && (
                          <Button size="small" variant="contained" color="success"
                            startIcon={<ArrowForwardIcon fontSize="small" />}
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              startConstruction(areaRow.id, "all");
                            }}>
                            全工程を開始
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            {isFiltered ? "工程フィルタ ON — 該当工程のみ表示" : "この施工箇所の工程一覧"}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: 120 }}>状態</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: 200 }} align="right">操作</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {visibleProcesses.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                                    工程マスタが登録されていません
                                  </TableCell>
                                </TableRow>
                              )}
                              {visibleProcesses.map((wd) => {
                                const wdDone = isProcessCompleted(db, areaRow.id, wd.id);
                                let procDraftConId: string | null = null;
                                if (!wdDone) {
                                  procDraftConId = getProcessDraftConstructionId(areaRow.id, wd.id, db);
                                }
                                return (
                                  <TableRow key={wd.id}>
                                    <TableCell>{wd.name}</TableCell>
                                    <TableCell>
                                      {wdDone ? (
                                        <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" sx={{ pointerEvents: "none" }} />
                                      ) : (
                                        <Chip size="small" variant="outlined" label="未完了" sx={{ pointerEvents: "none" }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {wdDone && (
                                        <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" sx={{ pointerEvents: "none" }} />
                                      )}
                                      {!wdDone && procDraftConId != null && (
                                        <Button size="small" variant="outlined" color="warning"
                                          startIcon={<PlayArrowIcon fontSize="small" />}
                                          sx={{ whiteSpace: "nowrap" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`${procDraftConId}/edit/`);
                                          }}>
                                          施工を再開
                                        </Button>
                                      )}
                                      {!wdDone && procDraftConId == null && (
                                        <Button size="small" variant="outlined" color="success"
                                          startIcon={<ArrowForwardIcon fontSize="small" />}
                                          sx={{ whiteSpace: "nowrap" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            startConstruction(areaRow.id, wd.id);
                                          }}>
                                          この工程を開始
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const hasCascadeFilter = workTypeFilter !== "" || workSubtypeFilter !== "" || workDetailFilter !== "";

  return (
    <Stack gap={2}>
      {/* 案内 Alert */}
      <Alert severity="info" sx={{ py: 0.5 }}>
        未施工のレイヤーを選択すると、その施工箇所を選択済にした「新規施工実施」フォームが開きます。「新規施工実施」からは空の状態で開始できます。
      </Alert>

      {/* 件数 Chip */}
      <Stack direction="row" justifyContent="flex-end">
        <Chip size="small" label={`${filteredAreas.length} 件の施工箇所`} />
      </Stack>

      {/* フィルタパネル */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, backgroundColor: "grey.50" }}>
        <Stack gap={1.5}>
          {/* 施工状況 */}
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>施工状況:</Typography>
            <FormControlLabel
              control={<Checkbox size="small" checked={statusFilter.has("pending")} onChange={() => toggleStatus("pending")} />}
              label="未施工"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={statusFilter.has("in_progress")} onChange={() => toggleStatus("in_progress")} />}
              label="施工中"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={statusFilter.has("done")} onChange={() => toggleStatus("done")} />}
              label="完了"
            />
          </Stack>

          {/* 施工日フィルタ */}
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>施工日フィルタ:</Typography>
            <RadioGroup row value={dateMode} onChange={(e) => {
              const v = e.target.value;
              if (v === "today" || v === "range" || v === "all") {
                setDateMode(v);
              }
            }}>
              <FormControlLabel value="today" control={<Radio size="small" />} label={`本日 (${todayStr})`} />
              <FormControlLabel value="range" control={<Radio size="small" />} label="期間指定" />
              <FormControlLabel value="all" control={<Radio size="small" />} label="全期間" />
            </RadioGroup>
            {dateMode === "range" && (
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  type="date" size="small" label="開始"
                  InputLabelProps={{ shrink: true }}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  sx={{ width: 160 }}
                />
                <Typography variant="body2" color="text.secondary">〜</Typography>
                <TextField
                  type="date" size="small" label="終了"
                  InputLabelProps={{ shrink: true }}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  sx={{ width: 160 }}
                />
              </Stack>
            )}
          </Stack>

          {/* 施工方法フィルタ */}
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>施工方法フィルタ:</Typography>
            <TextField
              select size="small" label="工種" sx={{ minWidth: 180 }}
              value={workTypeFilter}
              onChange={(e) => setWorkTypeFilter(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <MenuItem value="">すべて</MenuItem>
              {workTypeOptions.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="種別" sx={{ minWidth: 180 }}
              value={workSubtypeFilter}
              disabled={workTypeFilter === ""}
              onChange={(e) => setWorkSubtypeFilter(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <MenuItem value="">すべて</MenuItem>
              {workSubtypeOptions.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="工程" sx={{ minWidth: 180 }}
              value={workDetailFilter}
              disabled={workSubtypeFilter === ""}
              onChange={(e) => setWorkDetailFilter(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <MenuItem value="">すべて</MenuItem>
              {workDetailOptions.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            {hasCascadeFilter && (
              <Button size="small" variant="text"
                onClick={() => { setWorkTypeFilter(""); setWorkSubtypeFilter(""); setWorkDetailFilter(""); }}>
                クリア
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* 場所区分階層ビュー */}
      {filteredAreas.length === 0 ? (
        <EmptyState message="該当する未施工施工箇所がありません" />
      ) : (
        <HierarchicalView
          builder={locationAxisBuilder}
          leafAction={handleLeaf}
          renderLeaves={renderLeavesTable}
          emptyText="該当する施工箇所がありません"
        />
      )}
    </Stack>
  );
}
